interface Props {
    inputPrefix: string;
    inputValue: string;
    nodeTypes?: string[];
}

export interface InputSeparationVariant {
    inputPrefix: string;
    inputValue: string;
}

// Common prefixes that should be treated as potentially valid
const COMMON_PREFIXES = ['node', 'category', 'document'];

export function parseInput({inputPrefix, inputValue, nodeTypes}: Props): InputSeparationVariant[] {
    // If input is empty, return empty (check this first)
    if (!inputValue) {
        return [];
    }

    // If there's already a prefix, return it as is
    if (inputPrefix) {
        return [{inputPrefix, inputValue}];
    }

    // If nodeTypes aren't loaded yet, can't parse
    if (!nodeTypes) {
        return [];
    }

    // Check if there's a colon separator
    const colonIndex = inputValue.indexOf(':');

    // No separator - return as is
    if (colonIndex === -1) {
        return [{inputPrefix: '', inputValue}];
    }

    // Find all possible valid separations
    const variants: InputSeparationVariant[] = [];
    let hasValidPrefixWithEmptyValue = false;

    // Find all colon positions and check each potential prefix
    for (let i = 0; i < inputValue.length; i++) {
        if (inputValue[i] === ':') {
            const potentialPrefix = inputValue.substring(0, i);
            const potentialValue = inputValue.substring(i + 1).trim();

            // Check if prefix is valid (in nodeTypes or common prefixes)
            const isValidPrefix =
                nodeTypes.includes(potentialPrefix.toLowerCase()) ||
                COMMON_PREFIXES.includes(potentialPrefix.toLowerCase());

            if (isValidPrefix) {
                // Special case: if there's nothing after the colon
                if (potentialValue === '') {
                    hasValidPrefixWithEmptyValue = true;
                } else {
                    variants.push({
                        inputPrefix: potentialPrefix,
                        inputValue: potentialValue
                    });
                }
            }
        }
    }

    // If we found valid variants with actual values, add the original as well
    if (variants.length > 0) {
        variants.push({inputPrefix: '', inputValue});
        return variants;
    }

    // If we only found a valid prefix with empty value (e.g., "node:")
    // Treat it as a regular search string, not as a prefix separator
    if (hasValidPrefixWithEmptyValue) {
        return [{inputPrefix: '', inputValue}];
    }

    // No valid prefix found - return original only
    return [{inputPrefix: '', inputValue}];
}
